/// <amd-module name="Search/Shared/Base/Container" />
import * as React from "react";
import { CompositeStore } from "Search/Shared/Base/Stores/CompositeStoresManager";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IStoresHub<TStoreName extends string, TAggregatedState> {
    getCompositeStore(storesNames: TStoreName[]): CompositeStore;

    getAggregatedState(): TAggregatedState;

    dispose(): void;
}

export interface IContainerProps<
    TStoreName extends string,
    TActionCreator,
    TAggregatedState,
    TStoresHub extends IStoresHub<TStoreName, TAggregatedState>> {
    actionCreator: TActionCreator;

    storesHub: TStoresHub;

    pageContext: IVssPageContext;

    isHosted: boolean;

    isMember: boolean;
}

export function create<
    TStoreName extends string,
    TActionCreator,
    TAggregatedState,
    TStoresHub extends IStoresHub<TStoreName, TAggregatedState>,
    TProps extends IContainerProps<TStoreName, TActionCreator, TAggregatedState, TStoresHub> = IContainerProps<TStoreName, TActionCreator, TAggregatedState, TStoresHub>>(
        stores: TStoreName[],
        render: (state: TAggregatedState, props: TProps) => JSX.Element | null,
        onDidUpdate?: (state: TAggregatedState, props: TProps) => void
    ): React.ComponentClass<TProps> {
    return class Container extends React.PureComponent<TProps, {}> {
        public componentDidMount(): void {
            this.props.storesHub.getCompositeStore(stores).addChangedListener(this.onStoreChanged);
        }

        public componentWillUnmount(): void {
            this.props.storesHub.getCompositeStore(stores).removeChangedListener(this.onStoreChanged);
        }

        public componentDidUpdate(): void {
            if (onDidUpdate) {
                onDidUpdate(this.props.storesHub.getAggregatedState(), this.props);
            }
        }

        public render(): JSX.Element | null {
            return render(this.props.storesHub.getAggregatedState(), this.props);
        }

        private onStoreChanged = (): void => {
            this.forceUpdate();
        }
    };
}
